import { useState } from 'react';
import type {
  KhachHang,
  KhoHang,
  PhieuXuatDieuChuyen,
  PhuongThucThanhToan,
  PhuongTienVanChuyen,
  QuyenChungTu
} from '@/types/schemas';

export interface FormFieldState {
  // Document information
  selectedDocument: QuyenChungTu | null;
  selectedDocumentNumber: string;
  selectedWarehouse: KhoHang | null;
  paymentMethod: PhuongThucThanhToan | string | null;

  // Customer and vehicle information
  selectedCustomer: KhachHang | null;
  selectedVehicle: PhuongTienVanChuyen | null;
}

export interface FormFieldActions {
  // Document setters
  setSelectedDocument: (document: QuyenChungTu | null) => void;
  setSelectedDocumentNumber: (documentNumber: string) => void;
  setSelectedWarehouse: (warehouse: KhoHang | null) => void;
  setPaymentMethod: (paymentMethod: PhuongThucThanhToan | string | null) => void;

  // Customer and vehicle setters
  setSelectedCustomer: (customer: KhachHang | null) => void;
  setSelectedVehicle: (vehicle: PhuongTienVanChuyen | null) => void;

  // Utility functions
  resetState: () => void;
  updateState: (updates: Partial<FormFieldState>) => void;
}

export interface UseFormFieldStateReturn {
  state: FormFieldState;
  actions: FormFieldActions;
}

const initialState: FormFieldState = {
  selectedDocument: null,
  selectedDocumentNumber: '',
  selectedWarehouse: null,
  paymentMethod: null,
  selectedCustomer: null,
  selectedVehicle: null
};

function transformInitialData(initialData?: PhieuXuatDieuChuyen | null): Partial<FormFieldState> {
  if (!initialData) return {};

  return {
    selectedDocument: initialData.ma_nk_data || null,
    selectedWarehouse: initialData.ma_kho_data || null,
    selectedCustomer: initialData.ma_kh_vc_data || null,
    selectedVehicle: initialData.ma_ptvc_data || null,
    paymentMethod: initialData.ma_pttt || null,
    selectedDocumentNumber: initialData.so_ct || ''
  };
}

export function useFormFieldState(initialData?: PhieuXuatDieuChuyen | null): UseFormFieldStateReturn {
  const [state, setState] = useState<FormFieldState>(() => ({
    ...initialState,
    ...transformInitialData(initialData)
  }));

  const actions: FormFieldActions = {
    setSelectedDocument: (selectedDocument: QuyenChungTu | null) => {
      setState(prev => ({ ...prev, selectedDocument }));
    },

    setSelectedDocumentNumber: (selectedDocumentNumber: string) => {
      setState(prev => ({ ...prev, selectedDocumentNumber }));
    },

    setSelectedWarehouse: (selectedWarehouse: KhoHang | null) => {
      setState(prev => ({ ...prev, selectedWarehouse }));
    },

    setPaymentMethod: (paymentMethod: PhuongThucThanhToan | string | null) => {
      setState(prev => ({ ...prev, paymentMethod }));
    },

    setSelectedCustomer: (selectedCustomer: KhachHang | null) => {
      setState(prev => ({ ...prev, selectedCustomer }));
    },

    setSelectedVehicle: (selectedVehicle: PhuongTienVanChuyen | null) => {
      setState(prev => ({ ...prev, selectedVehicle }));
    },

    resetState: () => {
      setState(initialState);
    },

    updateState: (updates: Partial<FormFieldState>) => {
      setState(prev => ({ ...prev, ...updates }));
    }
  };

  return { state, actions };
}
